/**
 * React Query хуки ресурса «Аномалии» (L3). Ключи namespaced отдельно от
 * analyticsKeys (['analytics']), чтобы инвалидации legacy-хуков не пересекались.
 */

import { useQuery } from '@tanstack/react-query'
import * as api from './anomalies.api'
import type { AnomalyFilterParams, AnomalyListParams } from './anomalies.api'

export const anomaliesKeys = {
  all: ['analytics-anomalies'] as const,
  summary: () => [...anomaliesKeys.all, 'summary'] as const,
  list: (p: AnomalyListParams) => [...anomaliesKeys.all, 'list', p] as const,
  detail: (id: string) => [...anomaliesKeys.all, 'detail', id] as const,
  recommendations: () => [...anomaliesKeys.all, 'recommendations'] as const,
  recommendationsV2: () => [...anomaliesKeys.all, 'recommendations-v2'] as const,
}

export function useAnomalySummary(filters: AnomalyFilterParams = {}) {
  return useQuery({
    queryKey: [...anomaliesKeys.summary(), filters],
    queryFn: () => api.getAnomalySummary(filters),
    staleTime: 5 * 60_000,
  })
}

export function useAnomalyList(params: AnomalyListParams) {
  return useQuery({
    queryKey: anomaliesKeys.list(params),
    queryFn: () => api.getAnomalyList(params),
    staleTime: 60_000,
  })
}

export function useAnomalyDetail(caseId: string | null) {
  return useQuery({
    queryKey: anomaliesKeys.detail(caseId ?? ''),
    queryFn: () => api.getAnomalyDetail(caseId!),
    enabled: !!caseId,
    staleTime: 5 * 60_000,
  })
}

/*
 * Хуки легаси-выдачи ГРУЗОВ удалены вместе с их ресурсом (см. комментарий в
 * `anomalies.api.ts`). Вкладка «Грузы» читает канонический слой через
 * `cargo-current.hooks.ts` с обязательным выбором группы сценариев.
 */

export function useRecommendations(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: anomaliesKeys.recommendations(),
    queryFn: () => api.getRecommendations(),
    staleTime: 5 * 60_000,
    enabled: options.enabled ?? true,
  })
}

/**
 * Recommendations V2. Ретраи выключены намеренно: запасной легаси-путь должен
 * включаться быстро, а не после нескольких повторов, — иначе вкладка надолго
 * зависает в загрузке при недоступном V2.
 */
export function useRecommendationsV2() {
  return useQuery({
    queryKey: anomaliesKeys.recommendationsV2(),
    queryFn: () => api.getRecommendationsV2(),
    staleTime: 5 * 60_000,
    retry: false,
  })
}
